import { useCallback, useState } from 'react';

import { useDisclosure } from '@/shared/hooks/useDisclosure';

type UseDashboardEditModalsParams = {
  hasChanges: boolean;
  onClose: () => void;
  resetToEmpty: () => void;
  deleteDashboard: () => void;
};

export const useDashboardEditModals = ({
  hasChanges,
  onClose,
  resetToEmpty,
  deleteDashboard
}: UseDashboardEditModalsParams) => {
  const discardDialog = useDisclosure();
  const removeAllDialog = useDisclosure();
  const removeDashboardDialog = useDisclosure();
  const removeWidgetDialog = useDisclosure();
  const saveErrorDialog = useDisclosure();
  const [removeWidgetCallback, setRemoveWidgetCallback] = useState<(() => void) | null>(null);
  const [hasDashboardNameErrors, setHasDashboardNameErrors] = useState(false);

  const handleRemoveAll = useCallback(() => {
    resetToEmpty();
    removeAllDialog.close();
  }, [resetToEmpty, removeAllDialog.close]);

  const handleRemoveDashboard = useCallback(() => {
    deleteDashboard();
  }, [deleteDashboard]);

  const openRemoveModal = useCallback(
    (onOk: () => void) => {
      setRemoveWidgetCallback(() => onOk);
      removeWidgetDialog.open();
    },
    [removeWidgetDialog.open]
  );

  const handleDiscardClick = useCallback(() => {
    if (hasChanges) {
      discardDialog.open();
    } else {
      onClose();
    }
  }, [hasChanges, onClose, discardDialog.open]);

  const handleConfirmRemoveWidget = useCallback(() => {
    if (removeWidgetCallback) {
      removeWidgetCallback();
      setRemoveWidgetCallback(null);
    }
    removeWidgetDialog.close();
  }, [removeWidgetCallback, removeWidgetDialog.close]);

  return {
    discardDialog,
    removeAllDialog,
    removeDashboardDialog,
    removeWidgetDialog,
    saveErrorDialog,
    removeWidgetCallback,
    hasDashboardNameErrors,
    setHasDashboardNameErrors,
    handleRemoveAll,
    handleRemoveDashboard,
    openRemoveModal,
    handleDiscardClick,
    handleConfirmRemoveWidget
  };
};
